type CatalogConfig = {
	category_id: number;
	category_locked: boolean;
	tag_id: number;
	tag_slug: string;
	limit: number;
	columns: number;
	featured: string;
	availability: string;
	search: string;
	orderby: string;
	order: string;
	pagination: boolean;
	filters: boolean;
	navigation: 'numbers' | 'arrows';
	sync_url: boolean;
};

type CatalogResponse = {
	success: boolean;
	data?: {
		html?: string;
	};
};

class InteractiveCatalog {
	private readonly root: HTMLElement;
	private readonly config: CatalogConfig;
	private controller: AbortController | null = null;

	public constructor( root: HTMLElement ) {
		this.root = root;
		this.config = JSON.parse( root.dataset.mlcConfig ?? '{}' ) as CatalogConfig;
		this.root.dataset.mlcReady = 'true';
		this.bind();
	}

	private bind(): void {
		this.root.querySelector<HTMLFormElement>( '[data-mlc-filters]' )?.addEventListener( 'submit', ( event ) => {
			event.preventDefault();
			void this.load( 1 );
		} );

		this.root.querySelectorAll<HTMLSelectElement>( '[data-mlc-filters] select' ).forEach( ( select ) => {
			select.addEventListener( 'change', () => void this.load( 1 ) );
		} );

		this.root.querySelector<HTMLElement>( '[data-mlc-reset]' )?.addEventListener( 'click', () => {
			this.root.querySelectorAll<HTMLInputElement | HTMLSelectElement>( '[data-mlc-filters] [name]' ).forEach( ( control ) => {
				control.value = 'mlc_category' === control.name ? '0' : '';
			} );
			void this.load( 1 );
		} );

		this.root.addEventListener( 'click', ( event ) => {
			const target = event.target instanceof Element ? event.target : null;
			const button = target?.closest<HTMLButtonElement>( '[data-mlc-page]' );
			if ( button && ! button.disabled ) {
				event.preventDefault();
				void this.load( Number.parseInt( button.dataset.mlcPage ?? '1', 10 ) );
				return;
			}

			const link = target?.closest<HTMLAnchorElement>( '.mlc-pagination a.page-numbers' );
			if ( link ) {
				event.preventDefault();
				const url = new URL( link.href, window.location.href );
				void this.load( Number.parseInt( url.searchParams.get( 'mlc_page' ) ?? '1', 10 ) );
			}
		} );
	}

	private async load( page: number ): Promise<void> {
		this.controller?.abort();
		this.controller = new AbortController();
		this.setLoading( true );

		const payload = this.payload( page );
		try {
			const response = await fetch( this.root.dataset.mlcAjaxUrl ?? '', {
				method: 'POST',
				body: payload,
				signal: this.controller.signal,
				credentials: 'same-origin',
			} );
			if ( ! response.ok ) {
				throw new Error( `Catalog request failed with status ${ response.status }.` );
			}

			const result = await response.json() as CatalogResponse;
			if ( ! result.success || ! result.data?.html ) {
				throw new Error( 'The catalog response did not contain updated results.' );
			}

			const wrapper = document.createElement( 'div' );
			wrapper.innerHTML = result.data.html.trim();
			const nextRoot = wrapper.firstElementChild;
			if ( ! ( nextRoot instanceof HTMLElement ) ) {
				throw new Error( 'The catalog response could not be rendered.' );
			}

			this.root.replaceWith( nextRoot );
			if ( this.config.sync_url ) {
				this.updateUrl( payload, page );
			}
			initializeCatalog( nextRoot );
			nextRoot.querySelector<HTMLElement>( '[data-mlc-feedback]' )?.focus();
		} catch ( error ) {
			if ( error instanceof DOMException && 'AbortError' === error.name ) {
				return;
			}
			this.setLoading( false );
			const feedback = this.root.querySelector<HTMLElement>( '[data-mlc-feedback]' );
			if ( feedback ) {
				feedback.textContent = 'Products could not be updated. Please try again.';
				feedback.classList.add( 'is-error' );
			}
		}
	}

	private payload( page: number ): FormData {
		const payload = new FormData();
		payload.set( 'action', 'mlc_catalog_query' );
		payload.set( 'nonce', this.root.dataset.mlcNonce ?? '' );
		Object.entries( this.config ).forEach( ( [ key, value ] ) => payload.set( key, String( value ) ) );
		payload.set( 'page', String( Math.max( 1, page ) ) );

		const form = this.root.querySelector<HTMLFormElement>( '[data-mlc-filters]' );
		if ( form ) {
			const values = new FormData( form );
			payload.set( 'search', String( values.get( 'mlc_search' ) ?? '' ) );
			payload.set( 'availability', String( values.get( 'mlc_availability' ) ?? '' ) );
			if ( ! this.config.category_locked ) {
				payload.set( 'category_id', String( values.get( 'mlc_category' ) ?? '0' ) );
			}
			const tagSelect = form.querySelector<HTMLSelectElement>( '[name="mlc_tag"]' );
			payload.set( 'tag_slug', String( values.get( 'mlc_tag' ) ?? '' ) );
			payload.set( 'tag_id', tagSelect?.selectedOptions[0]?.dataset.tagId ?? '0' );
		}

		return payload;
	}

	private updateUrl( payload: FormData, page: number ): void {
		const url = new URL( window.location.href );
		const values: Record<string, string> = {
			mlc_search: String( payload.get( 'search' ) ?? '' ),
			mlc_category: this.config.category_locked ? '' : String( payload.get( 'category_id' ) ?? '' ),
			mlc_tag: String( payload.get( 'tag_slug' ) ?? '' ),
			mlc_availability: String( payload.get( 'availability' ) ?? '' ),
			mlc_page: page > 1 ? String( page ) : '',
		};
		Object.entries( values ).forEach( ( [ key, value ] ) => {
			if ( '' === value || '0' === value ) {
				url.searchParams.delete( key );
			} else {
				url.searchParams.set( key, value );
			}
		} );
		window.history.replaceState( {}, '', url );
	}

	private setLoading( loading: boolean ): void {
		this.root.classList.toggle( 'is-loading', loading );
		this.root.setAttribute( 'aria-busy', String( loading ) );
		const feedback = this.root.querySelector<HTMLElement>( '[data-mlc-feedback]' );
		if ( feedback ) {
			feedback.textContent = loading ? 'Loading products…' : '';
			feedback.classList.remove( 'is-error' );
		}
	}
}

function initializeCatalog( root: HTMLElement ): void {
	if ( 'true' !== root.dataset.mlcReady ) {
		new InteractiveCatalog( root );
	}
}

document.querySelectorAll<HTMLElement>( '[data-mlc-catalog]' ).forEach( initializeCatalog );
